type RandomTeam = {
  id?: string | null;
};

type RandomGame = {
  homeTeam?: RandomTeam | null;
  awayTeam?: RandomTeam | null;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
};

type RandomBet = {
  betType: string;
  displayInfo?: unknown;
};

export type RandomPickSelection = {
  selectedTeamId: string;
  side: string;
  tiebreakerGuess?: number;
  label?: string;
};

const TIEBREAKER_GUESS_DISTANCE = 15;

const randomIntegerBetween = (min: number, max: number, random: () => number): number => {
  return Math.floor(random() * (max - min + 1)) + min;
};

const parseDisplayNumber = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number.parseFloat(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const getDisplayInfo = (bet: RandomBet): Record<string, unknown> => {
  return bet.displayInfo && typeof bet.displayInfo === "object"
    ? (bet.displayInfo as Record<string, unknown>)
    : {};
};

export const createRandomPickForBet = (
  game: RandomGame,
  bet: RandomBet,
  random: () => number = Math.random,
): RandomPickSelection => {
  const displayInfo = getDisplayInfo(bet);
  const homeTeamId = game.homeTeam?.id ?? game.homeTeamId ?? "";
  const awayTeamId = game.awayTeam?.id ?? game.awayTeamId ?? "";

  if (bet.betType === "total_tiebreaker") {
    const total = parseDisplayNumber(displayInfo["total"]);
    const tiebreakerGuess =
      total === null
        ? randomIntegerBetween(20, 80, random)
        : total + randomIntegerBetween(-TIEBREAKER_GUESS_DISTANCE, TIEBREAKER_GUESS_DISTANCE, random);

    return {
      selectedTeamId: homeTeamId,
      side: "one",
      tiebreakerGuess,
      label: "Random tiebreaker",
    };
  }

  if (bet.betType === "custom_prop") {
    const options = Array.isArray(displayInfo["options"]) ? displayInfo["options"] : [];
    const validOptions = options.filter(
      (option): option is { side: string; label?: string } => {
        const side = option && typeof option === "object" ? (option as { side?: unknown }).side : undefined;
        return typeof side === "string" && side.length > 0;
      },
    );

    if (validOptions.length > 0) {
      const option = validOptions[randomIntegerBetween(0, validOptions.length - 1, random)]!;
      return {
        selectedTeamId: option.side,
        side: option.side,
        label: typeof option.label === "string" ? option.label : option.side,
      };
    }
  }

  const side = random() < 0.5 ? "one" : "two";
  return {
    selectedTeamId: side === "one" ? homeTeamId : awayTeamId,
    side,
    label: side === "one" ? "Side one" : "Side two",
  };
};
